// components/attendance/components/AttendanceTable.js
import React, { useState } from 'react';
import Icon from '../../../components/appIcon';
import Image from '../../../components/appImage';
import PPEStatusBadge from '../../../components/ppeStatusBadge';
import Button from '../../../components/ui/button';
import ColumnVisibilityModal from './columnVisibilityModal';
import AttendanceRowDetails from './attendanceRowDetails';
import AttendanceViewModal from './attendanceViewModal';
import StatusBadge from './statusBadge';
const BACKEND_URL = import.meta.env.VITE_API_URL;

const AttendanceTable = ({
  records,
  onSort,
  sortConfig,
  onBulkAction,
  selectedRecords,
  onSelectRecord,
  onSelectAll,
  onEditClick,
  onDeleteClick,
  onViewClick,
  showEmployeeColumn,
  onSwitchClick,
  detectionMode
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    employee: true,
    checkIn: true,
    checkOut: true,
    duration: true,
    status: true,
    ppeStatus: detectionMode === "FULL_KIT"  ?  true : false, // Add PPE status column
    location: true,
    image: true,
    actions: true
  });

  const handleRowExpand = (recordId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(recordId)) {
      newExpanded?.delete(recordId);
    } else {
      newExpanded?.add(recordId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (column) => {
    onSort(column);
  };

  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleViewClick = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);

    // Also call the original onViewClick if provided
    if (onViewClick) {
      onViewClick(record);
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--';
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '--';
    const diff = new Date(checkOut) - new Date(checkIn);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const allSelected = records?.length > 0 && selectedRecords?.length === records?.length;
  const someSelected = selectedRecords?.length > 0 && selectedRecords?.length < records?.length;

  // Parse PPE items if they're strings
  const parsePPEItems = (items) => {
    if (!items) return [];
    return typeof items === 'string' ? JSON.parse(items) : items;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header Actions */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll(e?.target?.checked)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <span className="text-sm text-muted-foreground">
              {selectedRecords?.length > 0 ? `${selectedRecords?.length} selected` : 'Select all'}
            </span>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          iconName="Settings"
          iconPosition="left"
          onClick={() => setShowColumnModal(true)}
        >
          Columns
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 p-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
              </th>
              <th className="text-left p-3">
                <span className="text-sm font-medium text-foreground">Employee ID</span>
              </th>
              {visibleColumns?.employee && (
                <th className="text-left p-3">
                  <button
                    onClick={() => handleSort('employee')}
                    className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                  >
                    <span>Employee</span>
                    <Icon name={getSortIcon('employee')} size={14} />
                  </button>
                </th>
              )}
              {visibleColumns?.checkIn && (
                <th className="text-left p-3">
                  <button
                    onClick={() => handleSort('checkIn')}
                    className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                  >
                    <span>Check In</span>
                    <Icon name={getSortIcon('checkIn')} size={14} />
                  </button>
                </th>
              )}
              {visibleColumns?.checkOut && (
                <th className="text-left p-3">
                  <button
                    onClick={() => handleSort('checkOut')}
                    className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                  >
                    <span>Check Out</span>
                    <Icon name={getSortIcon('checkOut')} size={14} />
                  </button>
                </th>
              )}
              {visibleColumns?.duration && (
                <th className="text-left p-3">
                  <span className="text-sm font-medium text-foreground">Duration</span>
                </th>
              )}
              {/* {visibleColumns?.status && (
                <th className="text-left p-3">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                  >
                    <span>Status</span>
                    <Icon name={getSortIcon('status')} size={14} />
                  </button>
                </th>
              )} */}
              {visibleColumns?.ppeStatus && (
                <th className="text-left p-3">
                  <span className="text-sm font-medium text-foreground">PPE Status</span>
                </th>
              )}
              {visibleColumns?.location && (
                <th className="text-left p-3">
                  <span className="text-sm font-medium text-foreground">Location</span>
                </th>
              )}
              {visibleColumns?.image && (
                <th className="text-left p-3">
                  <span className="text-sm font-medium text-foreground">Image</span>
                </th>
              )}
              {visibleColumns?.actions && (
                <th className="text-right p-3">
                  <span className="text-sm font-medium text-foreground">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {records?.map((record) => (
              <React.Fragment key={record?.id}>
                <tr className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedRecords?.includes(record?.id)}
                      onChange={(e) => onSelectRecord(record?.id, e?.target?.checked)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                  </td>
                  <td className="p-3">
                    <div>
                      <div className="text-sm text-muted-foreground">{record?.employee?.id}</div>
                    </div>
                  </td>
                  {visibleColumns?.employee && (
                    <td className="p-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-muted rounded-full overflow-hidden">
                          <Image
                            src={`${BACKEND_URL}${record?.employee?.avatar}`}
                            alt={record?.employee?.name}
                            className="object-cover h-full"
                          />
                        </div>
                        <div>
                          <div className="font-medium text-foreground">{record?.employee?.name}</div>
                        </div>
                      </div>
                    </td>
                  )}
                  {visibleColumns?.checkIn && (
                    <td className="p-3">
                      <div className="text-sm text-foreground">{formatTime(record?.checkIn)}</div>
                      <div className="text-xs text-muted-foreground">
                        {record?.checkIn ? new Date(record.checkIn)?.toLocaleDateString() : '--'}
                      </div>
                    </td>
                  )}
                  {visibleColumns?.checkOut && (
                    <td className="p-3">
                      <div className="text-sm text-foreground">{formatTime(record?.checkOut)}</div>
                      <div className="text-xs text-muted-foreground">
                        {record?.checkOut ? new Date(record.checkOut)?.toLocaleDateString() : '--'}
                      </div>
                    </td>
                  )}
                  {visibleColumns?.duration && (
                    <td className="p-3">
                      <div className="text-sm font-mono text-foreground">
                        {formatDuration(record?.checkIn, record?.checkOut)}
                      </div>
                    </td>
                  )}
                  {/* {visibleColumns?.status && (
                    <td className="p-3">
                      <StatusBadge status={record?.status} />
                    </td>
                  )} */}
                  {visibleColumns?.ppeStatus && (
                    <td className="p-3">
                      <PPEStatusBadge
                        status={record?.ppeStatus}
                        missingItems={parsePPEItems(record?.missingPpeItems)}
                      />
                    </td>
                  )}
                  {visibleColumns?.location && (
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <Icon name="MapPin" size={14} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{record?.location}</span>
                      </div>
                    </td>
                  )}
                  {visibleColumns?.image && (
                    <td className="p-3">
                      {record?.captureImage ? (
                        <div className="w-12 h-12 bg-muted rounded overflow-hidden">
                          <Image
                            src={`${BACKEND_URL}${record?.captureImage}`}
                            alt="Attendance capture"
                            className="object-cover cursor-pointer hover:opacity-80 h-full"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                          <Icon name="ImageOff" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </td>
                  )}
                  {visibleColumns?.actions && (
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewClick(record)}
                          title="View Attendance"
                        >
                          <Icon name="View" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditClick(record)}
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteClick(record)}
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                        {/* <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onSwitchClick(record)}
                          title="Switch Person"
                        >
                          <Icon name="UserSwitch" size={16} />
                        </Button> */}
                      </div>
                    </td>
                  )}
                </tr>
                {expandedRows?.has(record?.id) && (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 2} className="p-0">
                      <AttendanceRowDetails record={record} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {records?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No attendance records found</h3>
          <p className="text-muted-foreground">Try adjusting your filters to see more results.</p>
        </div>
      )}

      {/* Column Visibility Modal */}
      {/* <ColumnVisibilityModal
        isOpen={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        visibleColumns={visibleColumns}
        onColumnToggle={handleColumnToggle}
      /> */}

      {/* View Attendance Modal */}
      <AttendanceViewModal
      detectionMode={detectionMode}
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        record={selectedRecord}
      />
    </div>
  );
};

export default AttendanceTable;